import { useCallback, useState, useId, type Dispatch, type FC, type SetStateAction } from 'react';
import { graphql, useFragment } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Popover } from 'dibs-elements/exports/Popover.tsx';
import { SbSharedQuickFilterChip } from '../SbSharedQuickFilterChip/SbSharedQuickFilterChip.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { isCategory } from '../../utils/categoryHelpers.ts';
import {
    getAppliedFilter,
    getFilterValues,
} from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';
import { showResults } from '../sbMessages.tsx';
import { QUICK_FILTER_TRACKING_OPTIONS } from '../SbSharedRefineMenu/sbSharedQuickFiltersConstants.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbRespQuickFilterDropdown.module.css';

import { type SbRespQuickFilterDropdown_viewer$key } from './__generated__/SbRespQuickFilterDropdown_viewer.graphql.ts';
import { type SbRespQuickFilterDropdown_itemSearch$key } from './__generated__/SbRespQuickFilterDropdown_itemSearch.graphql.ts';
import { type SbRespQuickFilterDropdown_user$key } from './__generated__/SbRespQuickFilterDropdown_user.graphql.ts';

const messages = defineMessages({
    close: { id: 'abf.SbRespQuickFilterDropdown.close', defaultMessage: 'Close' },
});

const viewerFragment = graphql`
    fragment SbRespQuickFilterDropdown_viewer on Viewer {
        ...SbSharedRefineMenuFilterMetal_viewer
        ...SbSharedRefineMenuFilterStone_viewer
        ...SbSharedRefineMenuFilterCreator_viewer
        ...SbSharedRefineMenuFilterMaterial_viewer
        ...SbSharedRefineMenuFilterItemLocation_viewer
        ...SbSharedRefineMenuFilterArtSubject_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment SbRespQuickFilterDropdown_itemSearch on ItemSearchQueryConnection {
        totalResults
        ...SbRespRefineMenuFilterPrice_itemSearch
        ...SbRespRefineMenuFilterCategory_itemSearch
        ...SbRespRefineMenuFilterArtSize_itemSearch
        ...SbSharedRefineMenuFilterStone_itemSearch
        ...SbSharedRefineMenuFilterMetal_itemSearch
        ...SbSharedRefineMenuFilterStyle_itemSearch
        ...SbSharedRefineMenuFilterArtSubject_itemSearch
        ...SbSharedRefineMenuFilterPeriod_itemSearch
        ...SbSharedRefineMenuFilterCreator_itemSearch
        ...SbSharedRefineMenuFilterMaterial_itemSearch
        ...SbSharedRefineMenuFilterItemLocation_itemSearch
        ...SbSharedRefineMenuFilterItemType_itemSearch
        ...SbSharedRefineMenuFilterJewelryType_itemSearch
        ...SbSharedRefineMenuFilterGender_itemSearch
        appliedFilters {
            name
            values {
                __typename
            }
        }
        filters {
            name
            localizedFilterName
            ...SbRespRefineMenuFilterCategory_filters
            ...SbRespRefineMenuFilterArtSize_filters
            values {
                displayName
                urlLabel
                count
                code
                linkReference
                linkable
                hexCode
                stats {
                    key
                    values
                }
                properties {
                    min
                    max
                }
            }
        }
    }
`;
const userFragment = graphql`
    fragment SbRespQuickFilterDropdown_user on User {
        ...SbRespRefineMenuFilterPrice_user
        ...SbRespRefineMenuFilterArtSize_user
    }
`;

type Props = {
    viewer: SbRespQuickFilterDropdown_viewer$key;
    itemSearch: SbRespQuickFilterDropdown_itemSearch$key;
    user: SbRespQuickFilterDropdown_user$key | null | undefined;
    isActive: boolean;
    isLoading: boolean;
    filterName: string;
    setActiveFilter: Dispatch<SetStateAction<string>>;
};

export const SbRespQuickFilterDropdown: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    isActive,
    isLoading,
    filterName,
    setActiveFilter,
}) => {
    const htmlId = useId();
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const intl = useIntl();
    const afterDisplay = useSbSelector(state => state.relayVariables.variables.afterDisplay);
    const currency = useSbSelector(state => state.currency.currency);
    const { appliedFilters, filters, totalResults } = itemSearch;
    const quickFilter = getAppliedFilter(filters, filterName);
    const { values } = quickFilter || {};
    let localizedFilterName = quickFilter?.localizedFilterName;
    if (filterName === 'location' && sbRespRefineMenuFiltersMap.location.title) {
        localizedFilterName = intl.formatMessage(sbRespRefineMenuFiltersMap.location.title);
    }

    const FilterComponent = sbRespRefineMenuFiltersMap[filterName]?.component;
    const appliedFilterCount = isCategory({ name: filterName, values: [] })
        ? 0
        : getFilterValues(appliedFilters || [], filterName).length;

    const togglePopover = useCallback(() => {
        setActiveFilter(isActive ? '' : filterName);
    }, [isActive, filterName, setActiveFilter]);
    const closePopover = useCallback(() => {
        setActiveFilter('');
    }, [setActiveFilter]);

    if (!localizedFilterName || !values || !filterName || !FilterComponent) {
        return null;
    }
    return (
        <>
            <div className={classnames(dibsCss.relative, dibsCss.zMiddle)}>
                <SbSharedQuickFilterChip
                    appliedFilterCount={appliedFilterCount}
                    isActive={isActive}
                    isLoading={isLoading}
                    linkRef={setAnchorEl}
                    localizedFilterName={localizedFilterName}
                    onClick={togglePopover}
                />

                <Popover
                    open={isActive}
                    onOpenChange={() => {}}
                    anchorEl={anchorEl}
                    align="start"
                    side="bottom"
                    showTriangle={false}
                    width="medium"
                    zIndex="high"
                >
                    <div className={classnames(dibsCss.flex, dibsCss.flexCol)}>
                        <div className={classnames(dibsCss.maxH300px, dibsCss.overflowYAuto)}>
                            <FilterComponent
                                key={`SbRespQuickFilterDropdown-${htmlId}-${isActive}`}
                                viewer={viewer}
                                itemSearch={itemSearch}
                                user={user}
                                currency={currency}
                                isClient={afterDisplay}
                                filterName={filterName}
                                filter={quickFilter}
                                filters={filters}
                                totalCount={0}
                                shouldUseExpandableList={false}
                                values={values}
                                showSearchBar
                                trackingOptions={QUICK_FILTER_TRACKING_OPTIONS}
                            />
                        </div>
                        <Link
                            className={classnames(
                                dibsCss.selfEnd,
                                dibsCss.textBodyMediumHeavy,
                                dibsCss.whitespaceNowrap,
                                {
                                    [styles.disabledText]: isLoading,
                                }
                            )}
                            onClick={closePopover}
                        >
                            {totalResults ? showResults(totalResults) : null}
                        </Link>
                    </div>
                </Popover>
            </div>
            {isActive && (
                <div
                    className={classnames(dibsCss.fixed, dibsCss.inset0, dibsCss.zLow)}
                    role="button"
                    tabIndex={0}
                    onClick={closePopover}
                    onKeyDown={() => {}}
                    aria-label={intl.formatMessage(messages.close)}
                />
            )}
        </>
    );
};
